/**
 * Tenant context — branding, settings, feature flags, permissions.
 *
 * IMPORTANT: this context is for RENDERING ONLY. The backend resolves the tenant
 * from the JWT claim; there is no tenant header or parameter anywhere in the API.
 * Never read `tenant.id` here to scope a request — if you find yourself wanting to,
 * the request is already tenant-scoped by the token.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { authApi } from '@/services/api/auth'
import { configureFormatting } from '@/lib/format'
import { useAuth } from './AuthProvider'
import type { Bootstrap, TenantBrand } from '@/types/api'

export interface TenantContextValue {
  isLoading: boolean
  tenant: TenantBrand | null
  branding: {
    accent: string | null
    logoUrl: string | null
    displayName: string
    poweredBy: string | null
  }
  settings: Record<string, unknown>
  featureFlags: Record<string, boolean>
  signatureRoles: string[]
  locales: string[]
  locale: string
  permissions: string[]
  role: string | null
}

const TenantContext = createContext<TenantContextValue | null>(null)

function toBooleanFlags(raw: Record<string, unknown> | undefined): Record<string, boolean> {
  const flags: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(raw ?? {})) flags[key] = Boolean(value)
  return flags
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { status, tenant: authTenant } = useAuth()
  const enabled = status === 'authenticated'

  const { data, isLoading } = useQuery<Bootstrap>({
    // Keyed by tenant id so a switch fetches fresh rather than serving the old one.
    queryKey: ['bootstrap', authTenant?.id ?? 'none'],
    queryFn: () => authApi.bootstrap(),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  })

  const value = useMemo<TenantContextValue>(() => {
    const brand = data?.tenant ?? null
    const settings = data?.settings ?? {}
    const locales = data?.locales?.length ? data.locales : ['en-IN']
    return {
      isLoading: enabled && isLoading,
      tenant: brand,
      branding: {
        accent: brand?.primary_color ?? null,
        logoUrl: brand?.logo_url ?? null,
        displayName: brand?.display_name ?? brand?.name ?? authTenant?.name ?? '',
        poweredBy: brand?.powered_by ?? null,
      },
      settings,
      featureFlags: toBooleanFlags(data?.feature_flags),
      signatureRoles: data?.signature_roles ?? [],
      locales,
      locale: locales[0] ?? 'en-IN',
      permissions: data?.permissions ?? [],
      role: data?.role ?? null,
    }
  }, [data, enabled, isLoading, authTenant])

  // Apply the tenant accent and locale as soon as bootstrap resolves.
  useEffect(() => {
    configureFormatting({ locale: value.locale })
  }, [value.locale])

  useEffect(() => {
    const root = document.documentElement
    const accent = value.branding.accent
    if (!accent) {
      root.style.removeProperty('--color-accent')
      root.removeAttribute('data-cta-style')
      return
    }
    root.style.setProperty('--color-accent', accent)

    // The backend validates the accent and derives `theme.cta_style` from the same
    // check its branding endpoint runs. It is authoritative: never re-derive it here,
    // or the two could disagree and a tenant would see different rules per surface.
    const ctaStyle = (value.tenant?.theme as Record<string, unknown> | undefined)?.cta_style
    if (ctaStyle === 'neutral') root.setAttribute('data-cta-style', 'neutral')
    else root.removeAttribute('data-cta-style')
  }, [value.branding.accent, value.tenant])

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) throw new Error('useTenant must be used inside <TenantProvider>')
  return context
}

/** Permissions for the current membership, straight from the backend. */
export function usePermissions(): string[] {
  return useTenant().permissions
}
